import { z } from 'zod';
import type { FulfillmentItem } from './shipping-form';

/** A warehouse, as much of it as recording a shipment needs. */
export type ManualShipProvider = {
  _id?: string;
  integration_type?: string;
  shipping_speed?: string[];
};

export type ManualShipProviderOption = {
  value: string;
  text: string;
  item?: ManualShipProvider;
};

export type ManualShipItem = FulfillmentItem & {
  content?: { item_status?: string; qty?: number; unit_price?: number };
  price?: number;
  quantity?: number;
};

/** What `POST shipping-labels/order/:id/ship` takes. */
export type ManualShipPayload = {
  fulfillment_providers: {
    fulfillment_provider_id: string;
    items: { item_id: string }[];
    package: string;
    provider: string;
    service: string;
    tracking_id: string;
  }[];
};

const CARRIER_REQUIRED = 'Shipping carrier is a required field.';
const TRACKING_REQUIRED = 'Tracking number is a required field.';

export const singleShipmentSchema = z.object({
  fulfillment_provider_id: z.string(),
  provider: z.string().trim().min(1, CARRIER_REQUIRED),
  tracking_number: z.string().trim().min(1, TRACKING_REQUIRED),
});

export type SingleShipmentValues = z.infer<typeof singleShipmentSchema>;

const shipmentDetailSchema = z.object({
  fulfillment_provider_id: z.string(),
  checked: z.boolean(),
  provider: z.string().trim(),
  tracking_number: z.string().trim(),
});

/** Carrier and tracking are asked for only on the rows that are shipping. */
export const multipleShipmentSchema = z
  .object({ details: z.array(shipmentDetailSchema) })
  .superRefine(({ details }, ctx) => {
    if (!details.some(detail => detail.checked)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Select at least one item',
        path: ['details'],
      });
    }

    details.forEach((detail, index) => {
      if (!detail.checked) {
        return;
      }
      if (!detail.provider) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CARRIER_REQUIRED,
          path: ['details', index, 'provider'],
        });
      }
      if (!detail.tracking_number) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: TRACKING_REQUIRED,
          path: ['details', index, 'tracking_number'],
        });
      }
    });
  });

export type MultipleShipmentValues = z.infer<typeof multipleShipmentSchema>;

/**
 * An integrated warehouse ships at a speed rather than with a carrier of our
 * choosing, so the field offers its speeds instead of the carrier list.
 */
export function carrierOptions(
  provider: ManualShipProvider | undefined,
  carriers: { value: string; label: string }[],
) {
  if (!provider || provider.integration_type === 'manual') {
    return { options: carriers, label: 'Shipping carrier' };
  }

  return {
    options: (provider.shipping_speed || []).map(speed => ({
      value: speed,
      label: speed.replace(/\b\w/g, letter => letter.toUpperCase()),
    })),
    label: 'Fulfillment type',
  };
}
